/**
 * Weekday × hour heatmap. 7 rows (Sun-Sat to match SQLite strftime %w),
 * 24 cols (0-23h). Cell color intensity = log-scaled count.
 *
 * Times are server-side UTC (the SQLite strftime computed UTC). We don't
 * re-bin client-side because that's expensive and the admin tz toggle
 * controls overall display elsewhere — for "when does this user log in"
 * UTC bucketing is fine for the V1.2 needs.
 */

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

interface Cell {
  weekday: number; // 0-6
  hour: number; // 0-23
  count: number;
}

interface Props {
  cells: readonly Cell[];
  height?: number;
}

export function HeatmapChart({ cells, height = 140 }: Props): JSX.Element {
  // Build a 7×24 matrix from the sparse input.
  const grid: number[][] = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
  for (const c of cells) {
    if (c.weekday >= 0 && c.weekday < 7 && c.hour >= 0 && c.hour < 24) {
      grid[c.weekday]![c.hour] = c.count;
    }
  }

  let max = 0;
  for (const row of grid) for (const v of row) if (v > max) max = v;
  if (max === 0) {
    return (
      <div
        className="w-full flex items-center justify-center text-xs text-slate-500"
        style={{ height }}
      >
        暂无登录记录
      </div>
    );
  }

  // Log scale so a few outlier-heavy hours don't make everything else look
  // identical.
  const intensity = (v: number): number => (v === 0 ? 0 : Math.log(v + 1) / Math.log(max + 1));

  const w = 600;
  const padL = 26; // 星期标签(中文)栏宽; 原 18 太窄, 窄屏上字会贴边被截
  const padT = 16;
  const padB = 14;
  const padR = 6;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;
  const cellW = innerW / 24;
  const cellH = innerH / 7;

  return (
    // 窄屏: 24 列热力图压扁后标签不可读, 给个最小宽度 + 横向滚动而不是硬挤.
    <div className="w-full overflow-x-auto">
    <svg
      viewBox={`0 0 ${w} ${height}`}
      className="w-full h-auto min-w-[460px]"
      preserveAspectRatio="none"
      style={{ overflow: 'visible' }}
      role="img"
      aria-label="登录时段热力图"
    >
      {/* Weekday labels (left) */}
      {WEEKDAY_LABELS.map((wd, row) => (
        <text
          key={row}
          x={padL - 7}
          y={padT + row * cellH + cellH / 2}
          fontSize={11}
          fill="rgb(100 116 139)"
          textAnchor="end"
          dominantBaseline="middle"
        >
          {wd}
        </text>
      ))}

      {/* Hour ticks (top) — every 6h */}
      {[0, 6, 12, 18].map((h) => (
        <text
          key={h}
          x={padL + h * cellW + cellW / 2}
          y={padT - 4}
          fontSize={9}
          fill="rgb(100 116 139)"
          textAnchor="middle"
        >
          {h}h
        </text>
      ))}

      {/* Cells */}
      {grid.map((row, r) =>
        row.map((count, h) => {
          const alpha = intensity(count);
          return (
            <g key={`${r}-${h}`}>
              <rect
                x={padL + h * cellW}
                y={padT + r * cellH}
                width={cellW - 1}
                height={cellH - 1}
                fill={count === 0 ? 'rgb(30 41 59)' : 'rgb(56 189 248)'}
                fillOpacity={count === 0 ? 0.3 : 0.15 + alpha * 0.85}
                rx={2}
              />
              {count > 0 && (
                <title>{`${WEEKDAY_LABELS[r]} ${h}:00 → ${count} 次登录 (UTC)`}</title>
              )}
            </g>
          );
        }),
      )}
    </svg>
    </div>
  );
}
